import "handsontable/styles/handsontable.css";
import "./opus-handsontable.css";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { HotTable, type HotTableRef } from "@handsontable/react-wrapper";
import { registerAllModules } from "handsontable/registry";
import { mainTheme, registerTheme, getTheme } from "handsontable/themes";

// Register Handsontable themes
registerTheme(mainTheme);
import {
  registerRenderer,
  textRenderer,
  checkboxRenderer,
  dropdownRenderer,
  autocompleteRenderer,
  numericRenderer,
} from "handsontable/renderers";
import type { ColumnSettings } from "handsontable/settings";
import type { CellChange, ChangeSource } from "handsontable/common";
import { Download } from "lucide-react";

import { useTheme } from "@/stores/theme";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./dialog";
import { Input } from "./input";
import { Label } from "./label";

registerAllModules();

/* ============================================ */
/* TEXT UTILITIES (port from app_core)          */
/* ============================================ */

function startCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const SPECIAL_CASES: Record<string, string> = {
  mac: "MAC",
  vlan: "VLAN",
  udld: "UDLD",
  dai: "DAI",
  stp: "STP",
  ssid: "SSID",
  dhcp: "DHCP",
  wan: "WAN",
  lldpCdp: "LLDP/CDP",
  ipv6: "IPv6",
  ipv4: "IPv4",
  "ipv4/ipv6": "IPv4/IPv6",
  ip: "IP",
  id: "ID",
  rf: "RF",
  ap: "AP",
  wpa: "WPA",
  mx: "MX",
  lan: "LAN",
};

const SPECIAL_CASES_PATTERNS = new Map<string, RegExp>(
  Object.entries(SPECIAL_CASES).map(([key]) => [key, new RegExp(startCase(key), "g")]),
);

function capitalizeString(str: string): string {
  if (str === "__opus_row_id__") return "ID";
  const lower = str.toLowerCase();
  const hits = Object.keys(SPECIAL_CASES).filter((k) => lower.includes(k));
  let result = startCase(str);
  for (const key of hits) {
    result = result.replace(SPECIAL_CASES_PATTERNS.get(key)!, SPECIAL_CASES[key]);
  }
  return result;
}

/* ============================================ */
/* RENDERER REGISTRY                            */
/* ============================================ */

// Keyed by HOT instance (WeakMap avoids GUID dependency and auto-cleans).
const _registry = new WeakMap<
  object,
  { changedIds: Set<string>; theme: "dark" | "light" }
>();

const RENDERER_NAME = "opusChangedRowRenderer";
let _rendererRegistered = false;

function ensureRendererRegistered() {
  if (_rendererRegistered) return;
  _rendererRegistered = true;

  registerRenderer(
    RENDERER_NAME,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (instance: any, td, row, col, prop, value, cellProps) => {
      // Dispatch to the correct base renderer first.
      const type: string = (cellProps as { type?: string }).type ?? "text";
      switch (type) {
        case "checkbox":
          checkboxRenderer(instance, td, row, col, prop, value, cellProps);
          break;
        case "dropdown":
          dropdownRenderer(instance, td, row, col, prop, value, cellProps);
          break;
        case "autocomplete":
          autocompleteRenderer(instance, td, row, col, prop, value, cellProps);
          break;
        case "numeric":
          numericRenderer(instance, td, row, col, prop, value, cellProps);
          break;
        default:
          textRenderer(instance, td, row, col, prop, value, cellProps);
      }

      // Apply green highlight if this row has been changed.
      const state = _registry.get(instance);
      if (!state?.changedIds.size) return;

      // Access the hidden ID by prop name — __opus_row_id__ is NOT in the
      // rendered columns array so positional indexing cannot be used.
      const rowId = String(
        instance.getDataAtRowProp(row, "__opus_row_id__") ?? "",
      );
      if (!rowId || !state.changedIds.has(rowId)) return;

      td.style.background =
        state.theme === "dark"
          ? "rgb(99 255 200 / 0.25)"
          : "rgb(28 169 122 / 0.20)";
    },
  );
}

ensureRendererRegistered();

/* ============================================ */
/* EMPTY DATA DETECTION                         */
/* ============================================ */

function isDataEffectivelyEmpty<T extends Record<string, unknown>>(
  data: T[],
): boolean {
  if (data.length === 0) return true;
  return data.every((row) =>
    Object.values(row).every((v) => v === null || v === undefined || v === ""),
  );
}

/* ============================================ */
/* PUBLIC TYPES                                 */
/* ============================================ */

export type OpusColumnConfig = (
  columnName: string,
) => Omit<Partial<ColumnSettings>, "data"> | undefined;

export interface OpusHandsontableProps<T extends Record<string, unknown>> {
  data: T[];
  /**
   * Key(s) used to build a stable synthetic row ID from the ORIGINAL data
   * snapshot. Composite keys are joined with `|`. Edits to the source
   * columns do not change the row's identity.
   */
  idConfig: (keyof T & string) | (keyof T & string)[];
  /** Additional column keys to hide (beyond the internal __opus_row_id__). */
  hiddenColumns?: string[];
  /**
   * Per-column Handsontable settings. Return `undefined` for defaults
   * (text, center-aligned, editable). The `data` field is always set
   * automatically and must not be returned.
   */
  columnConfig?: OpusColumnConfig;
  /** Default CSV filename shown in the export dialog. */
  exportFileName?: string;
  height?: number | string;
  className?: string;
  /**
   * Number of blank editable rows to display when `data` is effectively
   * empty (length 0, or every cell is null / undefined / ""). Requires
   * `columnKeys` to define the column schema.
   */
  defaultEmptyRows?: number;
  /**
   * Explicit column key list. Used as the column schema when data is empty
   * and `defaultEmptyRows > 0`. Ignored when data is non-empty (columns are
   * derived from `data[0]` in that case).
   */
  columnKeys?: string[];
  /**
   * Apply Aurora-themed custom styles (fonts, typography, visual polish).
   * When false, uses vanilla Handsontable styling. Defaults to true.
   * Dark/light mode is handled by Handsontable's theme system via the theme prop.
   */
  useCustomStyles?: boolean;
}

/* ============================================ */
/* HELPERS (module-scope to keep component ≤300 lines) */
/* ============================================ */

interface HotSettingsParams {
  processedData: Record<string, unknown>[];
  colHeaders: string[];
  columns: ColumnSettings[];
  hiddenColumnIndices: number[];
  height: number | string;
  useCustomStyles: boolean;
  theme: "dark" | "light";
}

function buildHotSettings({
  processedData,
  colHeaders,
  columns,
  hiddenColumnIndices,
  height,
  useCustomStyles,
  theme,
}: HotSettingsParams) {
  return {
    data: processedData,
    colHeaders,
    columns,
    hiddenColumns: {
      columns: hiddenColumnIndices,
      indicators: false,
      copyPasteEnabled: false,
    },
    height,
    renderAllRows: false,
    viewportRowRenderingOffset: 15,
    viewportColumnRenderingOffset: 2,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dropdownMenu: ["filter_by_condition", "filter_by_value", "filter_action_bar"] as any,
    filters: true,
    columnSorting: true,
    manualColumnResize: true,
    stretchH: "all" as const,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contextMenu: ["undo", "redo", "---------", "copy", "cut"] as any,
    cells: () => ({ renderer: RENDERER_NAME }),
    licenseKey: "non-commercial-and-evaluation",
    width: "100%",
    theme: useCustomStyles
      ? getTheme("main")?.setColorScheme(theme === "dark" ? "dark" : "light")
      : undefined,
  };
}

function CsvExportDialog({
  open,
  onOpenChange,
  csvFilename,
  setCsvFilename,
  exportFileName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  csvFilename: string;
  setCsvFilename: (v: string) => void;
  exportFileName: string;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Export as CSV</DialogTitle>
        <DialogDescription>
          Enter a filename for the download. Hidden columns are excluded.
        </DialogDescription>
        <div className="mt-4 flex flex-col gap-1.5">
          <Label htmlFor="opus-hot-csv-name">Filename</Label>
          <Input
            id="opus-hot-csv-name"
            value={csvFilename}
            onChange={(e) => setCsvFilename(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onConfirm();
            }}
            placeholder={exportFileName}
          />
        </div>
        <div className="mt-5 flex justify-end gap-2.5">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================ */
/* COMPONENT                                    */
/* ============================================ */

const EMPTY_HIDDEN_COLUMNS: string[] = [];

export function OpusHandsontable<T extends Record<string, unknown>>({
  data,
  idConfig,
  hiddenColumns: hiddenColumnsProp = EMPTY_HIDDEN_COLUMNS,
  columnConfig,
  exportFileName = "export",
  height = 420,
  className,
  defaultEmptyRows = 0,
  columnKeys,
  useCustomStyles = true,
}: OpusHandsontableProps<T>) {
  const theme = useTheme((s) => s.resolved);
  const hotRef = useRef<HotTableRef | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Mutable refs — never cause React re-renders.
  const changedIdsRef = useRef<Set<string>>(null!);
  if (changedIdsRef.current == null) { changedIdsRef.current = new Set(); }
  const themeRef = useRef<"dark" | "light">(theme);
  const originalDataMapRef = useRef<Map<string, Record<string, unknown>>>(null!);
  if (originalDataMapRef.current == null) { originalDataMapRef.current = new Map(); }

  // Ref copy of columnOrder so afterChange can read the latest without
  // a useCallback dependency that would trigger HOT updateSettings.
  const columnOrderRef = useRef<string[]>([]);

  const instanceId = useId();
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  // Capture the initial exportFileName once on mount so csvFilename is not
  // re-derived on each render (avoiding no-derived-useState).
  const initExportFileNameRef = useRef(exportFileName);
  // eslint-disable-next-line react-hooks/refs
  const [csvFilename, setCsvFilename] = useState(initExportFileNameRef.current);

  // Keep themeRef always current so stable callbacks read the latest value.
  useEffect(() => {
    themeRef.current = theme;
  });

  /* ───────── id keys ───────────────────────── */

  const idKeys = useMemo(
    () => (Array.isArray(idConfig) ? idConfig : [idConfig]),
    [idConfig],
  );

  /* ───────── processed data ────────────────── */

  const processedData = useMemo((): Record<string, unknown>[] => {
    const isEmpty = isDataEffectivelyEmpty(data);

    if (isEmpty && defaultEmptyRows > 0 && columnKeys?.length) {
      // eslint-disable-next-line react-hooks/refs
      return Array.from({ length: defaultEmptyRows }, (_, i) => {
        const rowId = `__empty_${instanceId}_${i}__`;
        const emptyRow = Object.fromEntries(columnKeys.map((k) => [k, ""]));
        if (!originalDataMapRef.current.has(rowId)) {
          originalDataMapRef.current.set(rowId, { ...emptyRow });
        }
        return { __opus_row_id__: rowId, ...emptyRow };
      });
    }

    // eslint-disable-next-line react-hooks/refs
    return data.map((row) => {
      const rowId = idKeys.map((k) => String(row[k] ?? "")).join("|");
      // Snapshot on first encounter only — preserve the original baseline.
      if (!originalDataMapRef.current.has(rowId)) {
        originalDataMapRef.current.set(rowId, {
          ...(row as Record<string, unknown>),
        });
      }
      return { __opus_row_id__: rowId, ...(row as Record<string, unknown>) };
    });
  }, [data, idKeys, defaultEmptyRows, columnKeys, instanceId]);

  /* ───────── column order ──────────────────── */

  // __opus_row_id__ is kept in the DATA (so getDataAtRowProp works) but is
  // intentionally excluded from the rendered `columns` array. Excluding it
  // avoids registering it with the hiddenColumns plugin, which would cause
  // HOT's filter dropdown to open for that hidden source column and
  // immediately auto-close, producing the "black artifact" bug.
  const columnOrder = useMemo((): string[] => {
    if (data.length > 0 && !isDataEffectivelyEmpty(data)) {
      return Object.keys(data[0]);
    }
    if (columnKeys?.length) {
      return [...columnKeys];
    }
    return [];
  }, [data, columnKeys]);

  // Keep the ref in sync for use inside afterChange without a dep.
  useEffect(() => {
    columnOrderRef.current = columnOrder;
  });

  /* ───────── column headers ────────────────── */

  const colHeaders = useMemo(
    () => columnOrder.map((k) => capitalizeString(k)),
    [columnOrder],
  );

  /* ───────── column settings ───────────────── */

  const columns = useMemo(
    () =>
      columnOrder.map((colName) => {
        const userConfig = columnConfig?.(colName) ?? {};
        return {
          type: "text",
          className: "htCenter",
          readOnly: false,
          ...userConfig,
          data: colName,
        } as ColumnSettings;
      }),
    [columnOrder, columnConfig],
  );

  /* ───────── hidden column indices ─────────── */

  // __opus_row_id__ is not in columnOrder, so no offset is needed.
  const hiddenColumnIndices = useMemo(() => {
    return hiddenColumnsProp.flatMap((name) => {
      const i = columnOrder.indexOf(name);
      return i !== -1 ? [i] : [];
    });
  }, [columnOrder, hiddenColumnsProp]);

  /* ───────── registry setup ────────────────── */

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    _registry.set(hot, {
      changedIds: changedIdsRef.current,
      theme: themeRef.current,
    });
    return () => {
      _registry.delete(hot);
    };
  }, []);

  /* ───────── theme sync ────────────────────── */

  useEffect(() => {
    themeRef.current = theme;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const entry = _registry.get(hot);
    if (entry) {
      entry.theme = theme;
      hot.render();
    }
  }, [theme]);

  /* ───────── tab-switching: ResizeObserver ─── */

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let prevWidth = el.getBoundingClientRect().width;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      if (prevWidth === 0 && w > 0) {
        hotRef.current?.hotInstance?.refreshDimensions();
      }
      prevWidth = w;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ───────── afterChange ───────────────────── */

  const handleAfterChange = useCallback(
    (changes: CellChange[] | null, source: ChangeSource) => {
      // "loadData" fires on initial population — skip it.
      // "UndoRedo" must NOT be skipped: undo fires afterChange and needs
      // to remove the highlight when a row reverts to its original state.
      if (!changes || source === "loadData") return;
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      const order = columnOrderRef.current;

      for (const [visualRow] of changes) {
        // Fetch the row ID by prop name — it lives in the source data but is
        // not part of the rendered columns array, so positional indexing
        // into getDataAtRow() would not find it.
        const rowId = String(
          hot.getDataAtRowProp(visualRow, "__opus_row_id__") ?? "",
        );
        if (!rowId) continue;

        const original = originalDataMapRef.current.get(rowId);
        if (!original) {
          changedIdsRef.current.add(rowId);
          continue;
        }

        // getDataAtRow returns values in `columns` order (= columnOrder,
        // which no longer includes __opus_row_id__), so indices align 1:1.
        const rowData = hot.getDataAtRow(visualRow) as unknown[];
        const dirty = order.some(
          (key, i) => String(rowData[i] ?? "") !== String(original[key] ?? ""),
        );

        if (dirty) {
          changedIdsRef.current.add(rowId);
        } else {
          changedIdsRef.current.delete(rowId);
        }
      }

      // Sync theme in registry in case it changed between renders.
      const entry = _registry.get(hot);
      if (entry) entry.theme = themeRef.current;

      hot.render();
    },
    // No deps: columnOrderRef and themeRef are refs, hotRef is stable.
    [],
  );

  /* ───────── HOT settings (all memoised) ──── */

  const hotSettings = useMemo(
    () => buildHotSettings({ processedData, colHeaders, columns, hiddenColumnIndices, height, useCustomStyles, theme }),
    [processedData, colHeaders, columns, hiddenColumnIndices, height, useCustomStyles, theme],
  );

  /* ───────── CSV export ────────────────────── */

  const handleExportConfirm = useCallback(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const plugin = hot.getPlugin("exportFile") as any;
    plugin?.downloadFile?.("csv", {
      bom: false,
      columnDelimiter: ",",
      colHeaders: true,
      exportHiddenColumns: false,
      exportHiddenRows: false,
      fileExtension: "csv",
      filename: csvFilename.trim() || exportFileName,
      mimeType: "text/csv",
      rowDelimiter: "\r\n",
      rowHeaders: false,
    });
    setCsvDialogOpen(false);
  }, [csvFilename, exportFileName, setCsvDialogOpen]);

  /* ───────── render ────────────────────────── */

  return (
    <div className={className}>
      {/* Toolbar */}
      <div className="mb-2 flex items-center justify-end">
        <Button
          variant="toolbar"
          size="sm"
          onClick={() => {
            setCsvFilename(exportFileName);
            setCsvDialogOpen(true);
          }}
        >
          <Download size={14} />
          Download CSV
        </Button>
      </div>

      {/* CSV export dialog */}
      <CsvExportDialog
        open={csvDialogOpen}
        onOpenChange={setCsvDialogOpen}
        csvFilename={csvFilename}
        setCsvFilename={setCsvFilename}
        exportFileName={exportFileName}
        onConfirm={handleExportConfirm}
      />

      {/* Handsontable wrapper — .opus-hot-wrap scopes the CSS overrides */}
      <div ref={wrapperRef} className={useCustomStyles ? "opus-hot-wrap" : ""}>
        <HotTable ref={hotRef} {...hotSettings} afterChange={handleAfterChange} />
      </div>
    </div>
  );
}
